<template>
    <div class="flex flex-col md:flex-row md:flex-wrap md:items-center xl:flex-nowrap xl:justify-between w-full gap-4">
        <div class="md:mr-4 body-small text-ink-muted">{{ t("analytics.filters.heading") }}</div>
        <SkeletonLoader v-if="isFetching" class="h-12.5 flex-1 rounded-lgr" />
        <BaseSelect
            v-for="filter in filterConfig"
            v-else
            :key="filter.filter_key"
            v-model="selectedFilters[filter.filter_key]"
            :icon="filter.icon"
            :placeholder="t('analytics.filters.allItems', { items: plural(filter.label) })"
            :options="filter.options"
            trigger-class="min-w-0"
            multiple />
    </div>
</template>
<script setup lang="ts">
import BaseSelect from "~vue/components/BaseSelect.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import _ from "lodash";
import { plural } from "pluralize";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { enqueueURLParamsUpdate } from "../../utils/enqueueURLParamsUpdate";
import type { SelectedSSOFilters, SSOFilterConfigResponse } from "../types";

const { t } = useI18n();

const selectedFilters = ref<SelectedSSOFilters>({});

const { config = null, isFetching = true } = defineProps<{
    config: SSOFilterConfigResponse | null;
    isFetching: boolean;
}>();

const filterConfig = computed(() => {
    if (!config) {
        return [];
    }

    return config.filters.map((filter) => ({
        ...filter,
        options: [{ key: null, label: t("analytics.filters.allItems", { items: plural(filter.label) }) }, ...filter.options],
    }));
});

// Watch the untranslated server config, not filterConfig, so a locale switch can't replace a selection
// whose URL update is still queued.
const serverFilters = computed(() => config?.filters);

// Apply current URL-param filter selections against fetched filter config
watch(serverFilters, (newConfig) => {
    const urlParams = new URLSearchParams(window.location.search);
    const newSelectedFilters: SelectedSSOFilters = {};

    if (newConfig) {
        for (const config of newConfig) {
            newSelectedFilters[config.filter_key] = [];
            const paramValues = urlParams.getAll(config.filter_key);
            for (const paramValue of paramValues) {
                if (config.options.some((o) => o.key !== null && o.key === paramValue) && !newSelectedFilters[config.filter_key].includes(paramValue)) {
                    newSelectedFilters[config.filter_key].push(paramValue);
                }
            }
        }
    }

    selectedFilters.value = newSelectedFilters;
});

const emit = defineEmits<{
    updated: [filters: SelectedSSOFilters];
}>();

// Unfortunately, with multi-select enabled, the new and old values in Vue's `watch` callback refer
// to the same value array instance, so we have to track the old value separately.
const oldSelectedFilters = ref<SelectedSSOFilters>({});

// Emit event and update URL search params when selected filters change
watch(
    selectedFilters,
    (newSelectedFilters) => {
        for (const filterKey of Object.keys(newSelectedFilters)) {
            if (Object.keys(oldSelectedFilters.value).includes(filterKey)) {
                const oldIncludesNull = oldSelectedFilters.value[filterKey].includes(null);
                const newIncludesNull = newSelectedFilters[filterKey].includes(null);
                if (!oldIncludesNull && newIncludesNull) {
                    // The user just selected the "All" (null) option, so clear any other selections
                    newSelectedFilters[filterKey] = [null];
                } else if (oldIncludesNull && newIncludesNull && newSelectedFilters[filterKey].length > oldSelectedFilters.value[filterKey].length) {
                    // The user just chose some other option besides "All", so automatically uncheck "All"
                    newSelectedFilters[filterKey] = newSelectedFilters[filterKey].filter((k) => k !== null);
                }
            }
            if (newSelectedFilters[filterKey].length === 0) {
                newSelectedFilters[filterKey] = [null];
            }
        }

        // Capture this now so we can avoid sending duplicate events to Amplitude below
        const isChanged = !_.isEqual(newSelectedFilters, oldSelectedFilters.value);

        oldSelectedFilters.value = _.cloneDeep(newSelectedFilters);
        emit("updated", newSelectedFilters);

        enqueueURLParamsUpdate((urlParams) => {
            for (const filterKey of Object.keys(newSelectedFilters)) {
                urlParams.delete(filterKey);
                const newValues = newSelectedFilters[filterKey];
                for (const value of newValues.values()) {
                    if (value !== null && value !== undefined) {
                        urlParams.append(filterKey, value);
                    }
                }
            }
            return urlParams;
        });

        if (isChanged) {
            const eventOptions: Record<string, string | number> = {};
            for (const [idx, cfg] of filterConfig.value.entries()) {
                eventOptions[`sso_key_${idx + 1}`] = cfg.filter_key;
                eventOptions[`selected_options_count_${idx + 1}`] = newSelectedFilters[cfg.filter_key]?.filter((o) => o !== null).length ?? 0;
            }
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_SSO_FILTERS_SELECTED, eventOptions);
        }
    },
    { deep: true, immediate: false },
);

// Expose reset function for parent to call when clearing filters
const reset = () => {
    const newSelectedFilters: SelectedSSOFilters = {};
    if (filterConfig.value) {
        for (const config of filterConfig.value) {
            newSelectedFilters[config.filter_key] = [null];
        }
    }
    selectedFilters.value = newSelectedFilters;
};

defineExpose({
    reset,
});
</script>
